import { Phone, PhoneOff } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { RetellWebClient } from "retell-client-js-sdk"

import { Button } from "@/components/ui/button"
import { SiriOrb } from "@/components/ui/siri-orb"
import { useAssistant } from "@/hooks/use-assistant"
import { cn } from "@/lib/utils"
import { testCallAgentPath } from "@/routes"

// IMP-360 rework: the browser-mic live-test-call panel, extracted from the
// receptionist's Connect step so the Lead Responder's "Test & go live" step
// renders literally the same component (per Chris's design note). Fetches a
// Retell web-call token from testCallAgentPath and streams the transcript —
// works pre-launch, before a phone number is provisioned.

// One entry per spoken segment in the Retell live transcript. The SDK
// fires `update` events with up to the last 5 sentences each time —
// we accumulate them locally so the panel shows the whole conversation
// instead of just the recent window.
interface TranscriptTurn {
  role: "agent" | "user"
  content: string
}

// Status of the active web call. "idle" is the default; "connecting"
// covers the fetch-token + SDK warmup window before audio actually
// flows; "live" once Retell fires `call_started`; "ending" while we
// wait for `call_ended`. Errors flip back to idle with a message.
type CallStatus = "idle" | "connecting" | "live" | "ending"

export function LiveTestCall({
  agentUid,
  title,
  idleDescription,
  speakerLabel,
  waitingMessage,
}: {
  agentUid: string
  // Optional bold panel title (the receptionist shows "Live test call"; the
  // lead responder's tab already names it).
  title?: string
  // Copy shown next to the button before a call is live.
  idleDescription: string
  // How the agent's transcript turns are labeled ("Receptionist", the agent
  // name, …).
  speakerLabel: string
  // Placeholder while connected but before the first transcript turn.
  waitingMessage: string
}) {
  const [callStatus, setCallStatus] = useState<CallStatus>("idle")
  const { theme: orbTheme } = useAssistant()
  const [callError, setCallError] = useState<string | null>(null)
  const [transcript, setTranscript] = useState<TranscriptTurn[]>([])
  // The RetellWebClient instance is held outside React state — its
  // identity doesn't drive renders, but we need it stable across
  // re-renders to clean up subscriptions in the teardown effect.
  const webClientRef = useRef<RetellWebClient | null>(null)
  const transcriptScrollRef = useRef<HTMLDivElement | null>(null)

  // Make sure we hang up if the user navigates away mid-call.
  useEffect(() => {
    return () => {
      webClientRef.current?.stopCall()
      webClientRef.current = null
    }
  }, [])

  // Auto-scroll the transcript pane as new turns arrive.
  useEffect(() => {
    if (transcriptScrollRef.current) {
      transcriptScrollRef.current.scrollTop =
        transcriptScrollRef.current.scrollHeight
    }
  }, [transcript])

  async function startTestCall() {
    setCallError(null)
    setTranscript([])
    setCallStatus("connecting")
    try {
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content")
      const response = await fetch(testCallAgentPath({ id: agentUid }), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken ?? "",
        },
        credentials: "same-origin",
      })
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as {
          error?: string
        }
        throw new Error(body.error ?? "Couldn't start the test call.")
      }
      const { access_token } = (await response.json()) as {
        access_token: string
      }

      const client = new RetellWebClient()
      webClientRef.current = client
      // Real-time transcript stream from Retell. Each update event
      // carries `update.transcript`, an array of { role, content }
      // objects covering the LAST 5 sentences — not the whole call.
      // We always replace local state with the latest snapshot rather
      // than appending, because every update is the canonical truth
      // for that window. (If a call exceeds the window we'll lose
      // earlier turns from the UI; acceptable for a test-call panel.)
      client.on("update", (update: { transcript?: TranscriptTurn[] }) => {
        if (Array.isArray(update.transcript)) {
          setTranscript(update.transcript)
        }
      })
      client.on("call_started", () => setCallStatus("live"))
      client.on("call_ended", () => {
        setCallStatus("idle")
        webClientRef.current = null
      })
      client.on("error", (err: unknown) => {
        console.error("[RetellWebClient]", err)
        setCallError(
          err instanceof Error ? err.message : "The test call hit an error.",
        )
        setCallStatus("idle")
        client.stopCall()
        webClientRef.current = null
      })

      await client.startCall({ accessToken: access_token })
    } catch (err) {
      console.error(err)
      setCallError(err instanceof Error ? err.message : String(err))
      setCallStatus("idle")
      webClientRef.current = null
    }
  }

  function endTestCall() {
    setCallStatus("ending")
    webClientRef.current?.stopCall()
    // The SDK fires call_ended which resets state — fall back in case it doesn't.
    setTimeout(() => {
      if (webClientRef.current) {
        webClientRef.current = null
        setCallStatus("idle")
      }
    }, 1500)
  }

  return (
    <div className="space-y-3">
      {/* Button drops below the copy on a phone: beside a shrink-0 orb AND a
          shrink-0 button, the description was down to one word per line. */}
      <div className="border-border flex flex-col gap-4 rounded-xl border p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-1 items-center gap-4">
          {/* IMP-461: the assistant's orb fronts the test call. */}
          <SiriOrb size={44} colors={orbTheme.colors} className="shrink-0" />
          <div className="min-w-0">
            {title && <p className="text-sm font-medium">{title}</p>}
            <p className="text-muted-foreground mt-0.5 text-sm">
              {callStatus === "live"
                ? "Connected — speak naturally into your mic."
                : idleDescription}
            </p>
          </div>
        </div>
        {callStatus === "live" || callStatus === "ending" ? (
          <Button
            variant="destructive"
            className="shrink-0"
            type="button"
            onClick={endTestCall}
            disabled={callStatus === "ending"}
          >
            <PhoneOff className="size-4" />
            {callStatus === "ending" ? "Ending…" : "End test call"}
          </Button>
        ) : (
          <Button
            variant="outline"
            className="shrink-0"
            type="button"
            onClick={() => void startTestCall()}
            disabled={callStatus === "connecting"}
          >
            <Phone className="size-4" />
            {callStatus === "connecting" ? "Connecting…" : "Start test call"}
          </Button>
        )}
      </div>

      {callError && <p className="text-destructive text-sm">{callError}</p>}

      {(transcript.length > 0 || callStatus !== "idle") && (
        <div
          ref={transcriptScrollRef}
          className="border-border bg-muted/30 max-h-64 space-y-2 overflow-y-auto rounded-lg border p-3"
        >
          {transcript.length === 0 ? (
            <p className="text-muted-foreground text-xs italic">
              {waitingMessage}
            </p>
          ) : (
            transcript.map((turn, i) => (
              <div key={i} className="text-sm">
                <span
                  className={cn(
                    "mr-2 inline-block text-xs font-semibold tracking-wide uppercase",
                    turn.role === "agent"
                      ? "text-blue-600 dark:text-blue-400"
                      : "text-muted-foreground",
                  )}
                >
                  {turn.role === "agent" ? speakerLabel : "You"}
                </span>
                <span>{turn.content}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}
